import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import EventIcon from "@mui/icons-material/Event";
import PlaceIcon from "@mui/icons-material/Place";
import RadioIcon from "@mui/icons-material/Radio";
import GroupIcon from "@mui/icons-material/Group";
import { AuthContext } from "../context/AuthContext";
import api from "../api/client";

const RESPONDED_EVENTS_KEY = "rvRespondedEvents";

// Load responded events from localStorage
const loadRespondedEvents = () => {
  try {
    const stored = localStorage.getItem(RESPONDED_EVENTS_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

// Save responded events to localStorage
const saveRespondedEvents = (events) => {
  try {
    localStorage.setItem(RESPONDED_EVENTS_KEY, JSON.stringify(events));
  } catch (e) {
    console.error("Failed to save responded events:", e);
  }
};

// Format date for CST display
const formatDateTimeCst = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    timeZone: "America/Chicago",
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function EventSignupModal() {
  const { user } = useContext(AuthContext);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchUpcomingEvents = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/patrol-events");
      const events = Array.isArray(res.data) ? res.data : [];
      
      // Filter events the user hasn't responded to yet
      const respondedEvents = loadRespondedEvents();
      const userId = user.id;
      
      // Filter to only upcoming, non-cancelled events user hasn't responded to
      const filteredEvents = events.filter((event) => {
        // Skip cancelled events
        if (event.isCancelled) return false;
        
        // Skip events the user is already signed up for
        if (event.signups?.some((s) => s.userId === userId)) return false;
        
        // Skip events user has already responded to (either in API or localStorage)
        if (event.responses?.some((r) => r.userId === userId)) return false;
        if (respondedEvents[`${userId}-${event.id}`]) return false;
        
        // Skip past events
        const endTime = new Date(event.endTimeUtc);
        if (endTime < new Date()) return false;
        
        return true;
      });
      
      setPendingEvents(filteredEvents);
      setCurrentEventIndex(0);
    } catch (err) {
      console.error("Failed to fetch events for modal:", err);
      setError("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchUpcomingEvents();
  }, [fetchUpcomingEvents]);

  const currentEvent = pendingEvents[currentEventIndex];

  const handleResponse = async (responseType) => {
    if (!currentEvent || !user) return;

    setSubmitting(true);
    try {
      // Record response in API
      await api.post(`/patrol-events/${currentEvent.id}/respond`, {
        responseType,
      });

      // Also save to localStorage as backup
      const respondedEvents = loadRespondedEvents();
      respondedEvents[`${user.id}-${currentEvent.id}`] = {
        responseType,
        respondedAt: new Date().toISOString(),
      };
      saveRespondedEvents(respondedEvents);

      // Move to next event or close modal
      if (currentEventIndex < pendingEvents.length - 1) {
        setCurrentEventIndex((prev) => prev + 1);
      } else {
        setPendingEvents([]);
      }
    } catch (err) {
      console.error("Failed to record response:", err);
      // Even on API error, save to localStorage and proceed
      const respondedEvents = loadRespondedEvents();
      respondedEvents[`${user.id}-${currentEvent.id}`] = {
        responseType,
        respondedAt: new Date().toISOString(),
      };
      saveRespondedEvents(respondedEvents);
      
      if (currentEventIndex < pendingEvents.length - 1) {
        setCurrentEventIndex((prev) => prev + 1);
      } else {
        setPendingEvents([]);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignUp = async () => {
    if (!currentEvent || !user) return;

    setSubmitting(true);
    try {
      // Sign up for the event
      await api.post(`/patrol-events/${currentEvent.id}/signup`, { notes: null });

      // Also record in responded events
      const respondedEvents = loadRespondedEvents();
      respondedEvents[`${user.id}-${currentEvent.id}`] = {
        responseType: "signed_up",
        respondedAt: new Date().toISOString(),
      };
      saveRespondedEvents(respondedEvents);

      // Move to next event or close modal
      if (currentEventIndex < pendingEvents.length - 1) {
        setCurrentEventIndex((prev) => prev + 1);
      } else {
        setPendingEvents([]);
      }
    } catch (err) {
      console.error("Failed to sign up:", err);
      setError(err.response?.data || "Failed to sign up");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = () => {
    handleResponse("declined");
  };

  const handleRemindLater = () => {
    // Just close without recording - will show again next time
    if (currentEventIndex < pendingEvents.length - 1) {
      setCurrentEventIndex((prev) => prev + 1);
    } else {
      setPendingEvents([]);
    }
  };

  // Don't show if no pending events
  if (!currentEvent || loading) {
    return null;
  }

  return (
    <Dialog
      open={Boolean(currentEvent)}
      onClose={handleRemindLater}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderTop: 4,
          borderColor: "primary.main",
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EventIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Upcoming Event
          </Typography>
          {pendingEvents.length > 1 && (
            <Chip
              label={`${currentEventIndex + 1} of ${pendingEvents.length}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Stack>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Event Image */}
        {currentEvent.imageUrl && (
          <Box
            sx={{
              width: "100%",
              height: 180,
              borderRadius: 2,
              overflow: "hidden",
              mb: 2,
            }}
          >
            <Box
              component="img"
              src={currentEvent.imageUrl}
              alt={currentEvent.title}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              onError={(e) => { e.target.parentElement.style.display = 'none'; }}
            />
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {currentEvent.title}
          </Typography>
          
          {currentEvent.eventType && currentEvent.eventType !== "Other" && (
            <Chip
              label={currentEvent.eventType}
              size="small"
              color="info"
              variant="outlined"
              sx={{ mb: 1 }}
            />
          )}
        </Box>

        {currentEvent.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {currentEvent.description}
          </Typography>
        )}

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <EventIcon fontSize="small" color="action" />
            <Typography variant="body2">
              <strong>When:</strong> {formatDateTimeCst(currentEvent.startTimeUtc)} – {formatDateTimeCst(currentEvent.endTimeUtc)} CST
            </Typography>
          </Stack>

          {(currentEvent.locationTitle || currentEvent.location) && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <PlaceIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Where:</strong>{" "}
                {currentEvent.locationTitle
                  ? `${currentEvent.locationTitle}${currentEvent.location ? ` - ${currentEvent.location}` : ""}`
                  : currentEvent.location}
              </Typography>
            </Stack>
          )}

          {currentEvent.region && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <GroupIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Region:</strong> {currentEvent.region}
              </Typography>
            </Stack>
          )}

          {currentEvent.radioChannel && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <RadioIcon fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Radio Channel:</strong> {currentEvent.radioChannel}
              </Typography>
            </Stack>
          )}
        </Stack>

        {/* Mini Map if coordinates exist */}
        {currentEvent.latitude && currentEvent.longitude && (
          <Box
            sx={{
              height: 150,
              width: "100%",
              borderRadius: 1,
              overflow: "hidden",
              mb: 2,
              border: 1,
              borderColor: "divider",
            }}
          >
            <iframe
              title={`Map for ${currentEvent.title}`}
              width="100%"
              height="100%"
              frameBorder="0"
              style={{ border: 0 }}
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${currentEvent.longitude - 0.01}%2C${currentEvent.latitude - 0.01}%2C${currentEvent.longitude + 0.01}%2C${currentEvent.latitude + 0.01}&layer=mapnik&marker=${currentEvent.latitude}%2C${currentEvent.longitude}`}
            />
          </Box>
        )}

        {/* Current signups */}
        {currentEvent.signups?.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
              Already Signed Up ({currentEvent.signups.length}):
            </Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {currentEvent.signups.slice(0, 10).map((signup) => (
                <Chip
                  key={signup.id}
                  size="small"
                  label={signup.name || signup.username || signup.rangerId}
                />
              ))}
              {currentEvent.signups.length > 10 && (
                <Chip size="small" label={`+${currentEvent.signups.length - 10} more`} />
              )}
            </Stack>
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Would you like to sign up for this event?
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleRemindLater} disabled={submitting}>
          Remind Me Later
        </Button>
        <Button onClick={handleDecline} color="inherit" disabled={submitting}>
          Not Interested
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSignUp}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
        >
          Sign Me Up
        </Button>
      </DialogActions>
    </Dialog>
  );
}
